import Swal from 'sweetalert2';
import { getObjectPlacement } from './backgroundFusionLayout';
import { computeHaloRect } from './computeHaloRect';
import { computeCaptionPlacement } from './computeCaptionPlacement';

const SKIP_CONFIRM_STORAGE_KEY = 'bgFusion.skipPlacementConfirm';
const CHECKBOX_ID = 'bgfusion-confirm-skip';

const COPY = {
  title: 'È tutto al punto giusto?',
  text:
    'Controlla che la foto sia <strong>dove</strong> e <strong>della dimensione</strong> con cui vuoi che si fonda: la fusione userà esattamente questa posizione.',
  captionPreview: 'Qui si fonderà, a questa dimensione',
  checkbox: 'Non mostrarmelo più',
  confirm: 'Sì, procedi',
  cancel: 'No, sistemo ancora',
};

const readSkipPreference = () => {
  try {
    return localStorage.getItem(SKIP_CONFIRM_STORAGE_KEY) === '1';
  } catch (err) {
    return false;
  }
};

const writeSkipPreference = () => {
  try {
    localStorage.setItem(SKIP_CONFIRM_STORAGE_KEY, '1');
  } catch (err) {
    // localStorage non disponibile: ignoriamo, il gate riapparirà.
  }
};

// Cattura la composizione (sfondo + oggetti) in spazio-canvas puro, immune a
// zoom/pan dell'editor: azzera temporaneamente il viewportTransform e lo
// ripristina sempre (finally).
const captureCanvasPreview = (canvas) => {
  const previousTransform = canvas.viewportTransform
    ? [...canvas.viewportTransform]
    : null;
  try {
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    return canvas.toDataURL({ format: 'jpeg', quality: 0.8, multiplier: 1 });
  } finally {
    if (previousTransform) {
      canvas.setViewportTransform(previousTransform);
    }
  }
};

// Frecce direzionali agli angoli dell'alone: pulsano verso l'esterno per
// comunicare la dimensione del riquadro in cui la foto si fonderà.
const HALO_ARROWS = ['tl', 'tr', 'bl', 'br']
  .map(
    (corner) =>
      `<span class="bgfusion-halo__arrow bgfusion-halo__arrow--${corner}"></span>`
  )
  .join('');

// Freccia della didascalia che, dall'alto della preview, indica la foto.
const CAPTION_ARROW_SVG =
  '<svg class="bgfusion-caption__arrow" width="22" height="20" viewBox="0 0 22 20" fill="none" aria-hidden="true">' +
  '<path d="M11 2 C11 9, 8 12, 11 17" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>' +
  '<path d="M6 12 L11 18 L16 12" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

// Didascalia ancorata al soggetto: la freccia punta sempre alla foto.
const buildCaptionHtml = (halo) => {
  const { placement, leftPct, anchorPct } = computeCaptionPlacement(halo);
  const vertical =
    placement === 'above' ? `bottom:${anchorPct}%` : `top:${anchorPct}%`;
  return `<div class="bgfusion-caption bgfusion-caption--${placement}" style="left:${leftPct}%;${vertical};">
      <span class="bgfusion-caption__pill">${COPY.captionPreview}</span>
      ${CAPTION_ARROW_SVG}
    </div>`;
};

const buildPreviewHtml = (previewDataUrl, halo) => `
  <div class="bgfusion-confirm-preview">
    <img src="${previewDataUrl}" alt="Anteprima composizione" />
    <div class="bgfusion-halo" style="left:${halo.leftPct}%;top:${
  halo.topPct
}%;width:${halo.widthPct}%;height:${halo.heightPct}%;">${HALO_ARROWS}</div>
    ${buildCaptionHtml(halo)}
  </div>`;

const buildDialogHtml = (previewDataUrl, halo) => {
  const preview =
    previewDataUrl && halo ? buildPreviewHtml(previewDataUrl, halo) : '';
  return `${preview}
    <p class="bgfusion-confirm-text">${COPY.text}</p>
    <label class="bgfusion-confirm-skip">
      <input type="checkbox" id="${CHECKBOX_ID}" />
      <span>${COPY.checkbox}</span>
    </label>`;
};

export default async function confirmBackgroundFusionPlacement({
  canvas,
  selection,
}) {
  if (readSkipPreference()) {
    return true;
  }

  let previewDataUrl = null;
  let halo = null;
  try {
    previewDataUrl = captureCanvasPreview(canvas);
    const placement = getObjectPlacement(selection, {
      width: canvas.width,
      height: canvas.height,
    });
    halo = computeHaloRect(placement);
  } catch (err) {
    // Anteprima non disponibile (canvas "tainted" o placement non calcolabile):
    // degrado morbido, mostriamo comunque la conferma testuale.
    previewDataUrl = null;
    halo = null;
  }

  const result = await Swal.fire({
    title: COPY.title,
    html: buildDialogHtml(previewDataUrl, halo),
    showCancelButton: true,
    reverseButtons: true,
    confirmButtonColor: '#278e8e',
    confirmButtonText: COPY.confirm,
    cancelButtonText: COPY.cancel,
    customClass: { popup: 'bgfusion-confirm-popup' },
    preConfirm: () => {
      const checkbox = document.getElementById(CHECKBOX_ID);
      return { skip: Boolean(checkbox && checkbox.checked) };
    },
  });

  if (result.isConfirmed) {
    if (result.value && result.value.skip) {
      writeSkipPreference();
    }
    return true;
  }

  return false;
}
